import { useState } from "react";
// ui
import {
  Typography,
  Box,
  TextField,
  Paper,
} from "@mui/material";
import { styled } from "@mui/system";
import EditNoteIcon from "@mui/icons-material/EditNote";

// InterFace
import { useAtomValue } from "jotai";
import { AppAtoms } from "../../lib/store";
// ★cmd-po_1205 (T4-a): frame→PoModalShell / footer→PoButton へ移行
import { PoModalShell } from "../common/PoModalShell";
import { PoButton } from "../common/PoButton";

interface PasswordEditModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  editPassword: {
    status: "before" | "editing" | "success" | "failed" | string;
    oldPassword: string;
    newPassword1: string;
    newPassword2: string;
  } | null;
  setEditPassword: (
    data: {
      status: "before" | "editing" | "success" | "failed" | string;
      oldPassword: string;
      newPassword1: string;
      newPassword2: string;
    } | null
  ) => void;
  putEditData: () => void;
}

const CustomTextField = styled(TextField)({
  "& .MuiInputBase-input": {
    fontSize: "12px",
    height: "12px",
  },
});
CustomTextField.defaultProps = {
  autoComplete: "off",
};

export function PasswordEditModal(props: PasswordEditModalProps) {
  const { onModal, swCloseModal, editPassword, setEditPassword, putEditData } =
    props;
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);

  // ★cmd-po_1205 (T4-a): footer を PoModalShell の footer スロットへ status 別に分離
  const footerNode = !editPassword ? undefined : editPassword.status === "before" ? (
    <>
      <PoButton
        variant="secondary"
        sx={{ textTransform: "none" }}
        onClick={swCloseModal}
      >
        キャンセル
      </PoButton>
      <PoButton
        disabled={
          editPassword.oldPassword === "" ||
          editPassword.newPassword1 === "" ||
          editPassword.newPassword2 === "" ||
          editPassword.newPassword1 !== editPassword.newPassword2
        }
        variant="primary"
        sx={{ textTransform: "none" }}
        onClick={putEditData}
      >
        <Box
          style={{
            color:
              editPassword.oldPassword === "" ||
              editPassword.newPassword1 === "" ||
              editPassword.newPassword2 === "" ||
              editPassword.newPassword1 !== editPassword.newPassword2
                ? "rgb(70 70 70)"
                : undefined,
          }}
        >
          編集
        </Box>
        <EditNoteIcon
          sx={{
            marginLeft: "6px",
            color:
              editPassword.oldPassword === "" ||
              editPassword.newPassword1 === "" ||
              editPassword.newPassword2 === "" ||
              editPassword.newPassword1 !== editPassword.newPassword2
                ? "rgb(70 70 70)"
                : "white",
          }}
          fontSize="medium"
        />
      </PoButton>
    </>
  ) : editPassword.status === "success" ? (
    <PoButton
      variant="secondary"
      sx={{ textTransform: "none" }}
      onClick={swCloseModal}
    >
      閉じる
    </PoButton>
  ) : editPassword.status === "failed" ||
    editPassword.status === "NotPolicy" ||
    editPassword.status === "Incorrect" ? (
    <PoButton
      variant="secondary"
      sx={{ textTransform: "none" }}
      onClick={() =>
        setEditPassword({
          ...editPassword,
          status: "before",
        })
      }
    >
      OK
    </PoButton>
  ) : undefined;

  return editPassword ? (
    <PoModalShell
      open={onModal}
      title="編集 - パスワード"
      onClose={swCloseModal}
      width="w760"
      testidPrefix="password-edit-modal"
      frameTestId="password-edit-modal-frame"
      modalTestId="password-edit-modal"
      footer={footerNode}
    >
        <Typography
          id="modal-modal-description"
          sx={{
            mt: 2,
            mb: 5,
            maxHeight: isSmartPhone ? "calc(92vh - 30px)" : undefined,
            overflowY: isSmartPhone ? "auto" : undefined,
          }}
        >
          <Paper elevation={0} data-testid="password-edit-form">
            {editPassword.status === "before" ? (
              <>
                <Box key="old-pass" data-testid="password-edit-old-field">
                  <Box data-testid="password-edit-old-label">旧パスワード</Box>
                  <CustomTextField
                    type={"password"}
                    id="textFieldId"
                    value={editPassword.oldPassword}
                    size="small"
                    onChange={(e) =>
                      setEditPassword({
                        ...editPassword,
                        oldPassword: e.target.value,
                      })
                    }
                    sx={{ width: "calc(100% - 10px)", margin: "10px 5px " }}
                  />
                </Box>
                <Box key="new-pass-1">
                  <Box>新規パスワード</Box>
                  <CustomTextField
                    type={"password"}
                    id="textFieldId"
                    value={editPassword.newPassword1}
                    size="small"
                    onChange={(e) =>
                      setEditPassword({
                        ...editPassword,
                        newPassword1: e.target.value,
                      })
                    }
                    sx={{ width: "calc(100% - 10px)", margin: "10px 5px " }}
                  />
                </Box>
                <Box key="new-pass-2">
                  <Box>新規パスワード（確認用）</Box>
                  <CustomTextField
                    type={"password"}
                    id="textFieldId"
                    value={editPassword.newPassword2}
                    size="small"
                    onChange={(e) =>
                      setEditPassword({
                        ...editPassword,
                        newPassword2: e.target.value,
                      })
                    }
                    sx={{ width: "calc(100% - 10px)", margin: "10px 5px " }}
                  />
                </Box>
              </>
            ) : editPassword.status === "success" ? (
              <>
                {/* ★U7(cmd-po_1108): result-state構造適用・shippori/m-plus除去 */}
                <Box className="result-state -success" data-testid="result-state-success">
                  <span className="rs-title">パスワードの編集に成功しました。</span>
                </Box>
              </>
            ) : editPassword.status === "failed" ? (
              <>
                {/* ★U7(cmd-po_1108): result-state構造適用・shippori/m-plus除去 */}
                <Box className="result-state -failed" data-testid="result-state-failed">
                  <span className="rs-title">
                    パスワードの編集に失敗しました。
                    <br />
                    データの形式が異なる可能性があります。
                  </span>
                </Box>
              </>
            ) : editPassword.status === "NotPolicy" ? (
              <>
                <Box className="result-state -failed" data-testid="result-state-failed">
                  <span className="rs-title">
                    パスワードの要件を満たしていません。
                    <br />
                    大文字・小文字・数字・記号を含む
                    <br />
                    8文字以上の文字列をご入力ください
                  </span>
                </Box>
              </>
            ) : editPassword.status === "Incorrect" ? (
              <>
                <Box className="result-state -failed" data-testid="result-state-failed">
                  <span className="rs-title">旧パスワードの情報が異なります。</span>
                </Box>
              </>
            ) : null}
          </Paper>
        </Typography>
    </PoModalShell>
  ) : null;
}
